import { useEffect, useState } from "react";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import { AnimatePresence, motion } from "framer-motion";
import classes from "./ImageGallery.module.scss";

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

const ImageGallery = ({ images = [] }) => {
  const [state, setState] = useState({
    image: "",
    index: 0,
    zoom: MIN_ZOOM,
    x: 0,
    y: 0,
  });

  const setZoom = (value) => {
    const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(value)));

    setState((prev) => ({
      ...prev,
      zoom: nextZoom,
      x: nextZoom <= MIN_ZOOM ? 0 : prev.x,
      y: nextZoom <= MIN_ZOOM ? 0 : prev.y,
    }));
  };

  const openImage = (image, index) => {
    setState({
      image: image.src,
      index,
      zoom: MIN_ZOOM,
      x: 0,
      y: 0,
    });
  };

  const closeImage = () => {
    setState({
      image: "",
      index: 0,
      zoom: MIN_ZOOM,
      x: 0,
      y: 0,
    });
  };

  const resetView = () => {
    setState((prev) => ({
      ...prev,
      zoom: MIN_ZOOM,
      x: 0,
      y: 0,
    }));
  };

  const nextImage = () => {
    setState((prev) => {
      const nextIndex = (prev.index + 1) % images.length;

      return {
        ...prev,
        image: images[nextIndex].src,
        index: nextIndex,
        zoom: MIN_ZOOM,
        x: 0,
        y: 0,
      };
    });
  };

  const prevImage = () => {
    setState((prev) => {
      const prevIndex = prev.index === 0 ? images.length - 1 : prev.index - 1;

      return {
        ...prev,
        image: images[prevIndex].src,
        index: prevIndex,
        zoom: MIN_ZOOM,
        x: 0,
        y: 0,
      };
    });
  };

  const zoomIn = () => {
    setZoom(state.zoom + ZOOM_STEP);
  };

  const zoomOut = () => {
    setZoom(state.zoom - ZOOM_STEP);
  };

  const handleWheel = (e) => {
    if (!state.image) return;

    e.preventDefault();

    if (e.deltaY < 0) {
      setZoom(state.zoom + ZOOM_STEP);
    } else {
      setZoom(state.zoom - ZOOM_STEP);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!state.image) return;

      if (e.key === "Escape") closeImage();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "+" || e.key === "=") setZoom(state.zoom + ZOOM_STEP);
      if (e.key === "-") setZoom(state.zoom - ZOOM_STEP);
      if (e.key.toLowerCase() === "r") resetView();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [state.image, state.zoom, images.length]);

  if (!images || images.length === 0) return null;

  const zoomPercent = Math.round(state.zoom * 100);

  return (
    <>
      <AnimatePresence>
        {state.image.length > 0 && (
          <motion.div
            className={classes["modal"]}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div
              className={classes["overlay"]}
              onClick={closeImage}
              aria-hidden="true"
            />

            <div className={classes["top-bar"]}>
              <div className={classes["image-count"]}>
                Image {state.index + 1} of {images.length}
              </div>

              <div className={classes["zoom-control"]}>
                <ZoomOutIcon />
                <input
                  type="range"
                  min={MIN_ZOOM}
                  max={MAX_ZOOM}
                  step={0.05}
                  value={state.zoom}
                  onChange={(e) => setZoom(e.target.value)}
                  aria-label="Zoom image"
                />
                <ZoomInIcon />
                <strong>{zoomPercent}%</strong>
              </div>

              <div className={classes["controls"]}>
                <button type="button" onClick={zoomOut} aria-label="Zoom out">
                  <ZoomOutIcon />
                </button>

                <button
                  type="button"
                  onClick={resetView}
                  aria-label="Reset image"
                >
                  <RestartAltIcon />
                </button>

                <button type="button" onClick={zoomIn} aria-label="Zoom in">
                  <ZoomInIcon />
                </button>

                <button
                  type="button"
                  onClick={closeImage}
                  aria-label="Close image"
                  className={classes["close-button"]}
                >
                  <CloseIcon />
                </button>
              </div>
            </div>

            <button
              type="button"
              className={classes["prevbtn"]}
              onClick={prevImage}
              aria-label="Previous image"
            >
              <NavigateBeforeIcon />
            </button>

            <div className={classes["stage"]} onWheel={handleWheel}>
              <AnimatePresence initial={false} mode="wait">
                <motion.img
                  key={state.index}
                  src={state.image}
                  alt={`Unified Building Group project ${state.index + 1}`}
                  drag={state.zoom > 1.05}
                  dragMomentum={false}
                  dragElastic={0.04}
                  onDragEnd={(_, info) => {
                    if (state.zoom <= 1.05) return;

                    setState((prev) => ({
                      ...prev,
                      x: prev.x + info.offset.x,
                      y: prev.y + info.offset.y,
                    }));
                  }}
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: 1,
                    scale: state.zoom,
                    x: state.zoom <= 1.05 ? 0 : state.x,
                    y: state.zoom <= 1.05 ? 0 : state.y,
                  }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.22 }}
                  className={state.zoom > 1.05 ? classes["zoomed"] : ""}
                />
              </AnimatePresence>
            </div>

            <button
              type="button"
              className={classes["nextbtn"]}
              onClick={nextImage}
              aria-label="Next image"
            >
              <NavigateNextIcon />
            </button>

            <div className={classes["hint"]}>
              Full image visible at 100%. Zoom with slider or scroll. Drag to
              pan.
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={classes["image-gallery"]}>
        {images.map((image, i) => (
          <button
            type="button"
            key={`${image.src}-${i}`}
            className={classes["thumb"]}
            onClick={() => openImage(image, i)}
          >
            <img
              src={image.src}
              alt={`Unified Building Group project ${i + 1}`}
            />
            <span>View</span>
          </button>
        ))}
      </div>
    </>
  );
};

export default ImageGallery;
